import {
  getQueryStringParams,
  setQueryStringParam,
  objectToQueryString,
  sortFunc,
  snapshotSquadsFromCache,
} from "../../shared/utils";
import { numberWithCommas, tooltipIfTruncated } from "../../lib/utils";
import { teamTreeExpansionMixin } from "../mixins/teamTreeExpansion";
import { peopleFiltersMixin } from "../mixins/peopleFilters";
import {
  percentageWidthForPosition as calcPercentageWidthForPosition,
  countForPosition as calcCountForPosition,
  workflowPercentageOfResponses as calcWorkflowPercentageOfResponses,
  targetedPositions as calcTargetedPositions,
  nonTargetedPositions as calcNonTargetedPositions,
  percentageOfResponses as calcPercentageOfResponses,
  workflowBenchmarkDiff as calcWorkflowBenchmarkDiff,
} from "./workflows/helpers";
import {
  renderWorkflowAverageValueTrendChart,
  renderWorkflowPercentInTargetRangeTrendChart,
  renderWorkflowAnnualizedCostTrendChart,
  renderWorkflowDistributionTrendChart,
  renderDriverTrendChart as renderDriverTrendChartPure,
  renderDriverDistributionTrendChart as renderDriverDistributionTrendChartPure,
} from "./workflows/chartRenderers";

export function snapshotWorkflows(defaults) {
  const {
    baseUrl,
    minResponseThreshold,
    drilldownTabs,
    maxBehavioralQuestions,
    tagOptions,
    groups,
    snapshotId,
  } = defaults;

  const targetedWorkflowId = getQueryStringParams().get(
    "behavioral_question_id",
  );

  const drilldownTab = getQueryStringParams().get("ddt");
  // Ensure valid drilldown tab is set
  if (drilldownTab && !drilldownTabs.some((t) => t.tab == drilldownTab)) {
    setQueryStringParam("ddt", "overview");
  }

  return {
    // Consolidated tree expansion behavior
    ...teamTreeExpansionMixin(),
    // Enable shared people filtering behavior
    ...peopleFiltersMixin(),

    // Getters for squad and group IDs from store
    get squadIds() {
      const storeValue = this.$store?.snapshotResultFilters?.squadIds;
      const urlValue =
        getQueryStringParams()
          .get("squad_ids")
          ?.split(",")
          .filter(Boolean)
          .map(Number) || [];
      return storeValue && storeValue.length > 0 ? [...storeValue] : urlValue;
    },

    get groupIds() {
      const storeValue = this.$store?.snapshotResultFilters?.groupIds;
      const urlValue =
        getQueryStringParams()
          .get("group_ids")
          ?.split(",")
          .filter(Boolean)
          .map(Number) || [];
      return storeValue && storeValue.length > 0 ? [...storeValue] : urlValue;
    },
    tooltipIfTruncated,

    // Base workflow properties
    minResponseThreshold,
    resultItemsLoaded: false,
    resultItems: [],
    commentSearchResults: [],
    commentSearchTerm: "",
    responseCount: null,
    recipientCount: null,
    drilldownModalOpen: !!targetedWorkflowId,
    targetedWorkflowId,
    drilldownTabs,
    groups,
    relatedDriver: null,
    trends: null,
    xStart: null,
    tableDataLoaded: false,
    isFetchingTableData: false,
    tabDataLoaded: false,
    maxBehavioralQuestions,
    selectedDrilldownTab: getQueryStringParams().get("ddt"),
    xEnd: null,
    selectedWorkflow: null,
    searchCommentsLoaded: false,
    relatedDriverTrends: [],
    drilldownData: {},
    squadNamesFromSquadIds: [],
    showTeamBreakdown: true,

    // Configure mixin id fields for this view
    rowIdKey: "snapshot_squad_id",
    cachedIdKey: "id",
    cachedParentKey: "parent_id",

    init() {
      // Sync store with URL params on init (Turbo can preserve stale store state)
      const params = getQueryStringParams();
      const squadIdsFromUrl =
        params.get("squad_ids")?.split(",").filter(Boolean).map(Number) || [];
      const groupIdsFromUrl =
        params.get("group_ids")?.split(",").filter(Boolean).map(Number) || [];

      this.$store.snapshotResultFilters.squadIds =
        groupIdsFromUrl.length > 0 ? [] : squadIdsFromUrl;
      this.$store.snapshotResultFilters.groupIds =
        squadIdsFromUrl.length > 0 ? [] : groupIdsFromUrl;
      this.$store.snapshotResultFilters.filterType =
        groupIdsFromUrl.length > 0 ? "groups" : "teams";

      // Set default sort for workflows if no sort is specified in URL
      if (!getQueryStringParams().get("sb")) {
        this.$store.snapshotResultFilters.sortBy = "avgValueBenchmark";
        this.$store.snapshotResultFilters.sortReverse = true;
        setQueryStringParam("sb", "avgValueBenchmark");
        setQueryStringParam("sr", "true");
      }

      this.$root.addEventListener("dx:squadsTree:init", () => {
        this.determineSquadNamesFromSquadIds();

        // Loading directly to modal from URL cached squads are empty, so wait for event
        if (this.squadIds.length != 1) {
          this.showTeamBreakdown = true;
        } else {
          this.showTeamBreakdown = this.cachedSquads.find(
            (squad) => squad.id == this.squadIds[0],
          )?.is_parent;
        }
      });

      this.fetchAll().then(() => {
        if (this.selectedDrilldownTab) {
          const selectedItem = drilldownTabs.find(
            (t) => t.tab == this.selectedDrilldownTab,
          );
          this.fetchTab(this.selectedDrilldownTab, selectedItem.url);
        }

        if (targetedWorkflowId) {
          this.selectedWorkflow = this.resultItems.find(
            (row) => row.id == this.targetedWorkflowId,
          );
        }

        if (getQueryStringParams().get("download_chart_img") === "true") {
          // slight delay to ensure rendering finishes
          setTimeout(() => {
            const workflowContainer = this.$root.querySelector(
              '[x-data="snapshotWorkflowsTimeCost()"]',
            );
            if (!workflowContainer) return;

            window.downloadAsImage(workflowContainer, "workflows-visual.png");

            // Remove the download image query param
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.delete("download_chart_img");
            const newUrl = `${window.location.pathname}${urlParams.toString() ? "?" + urlParams.toString() : ""}`;
            window.history.replaceState({}, "", newUrl);
          }, 300);
        }
      });

      this.$watch("$store.snapshotResultFilters.compareTo", () => {
        this.handleComparisonChange();
      });
      this.$watch("$store.snapshotResultFilters.previousSnapshotId", () => {
        this.handleComparisonChange();
      });
    },
    get cachedSquads() {
      return snapshotSquadsFromCache(snapshotId);
    },
    get filtersApplied() {
      return (
        this.$store?.snapshotResultFilters?.tagIds.length ||
        this.$store?.snapshotResultFilters?.squadIds.length ||
        this.$store?.snapshotResultFilters?.groupIds.length
      );
    },
    determineSquadNamesFromSquadIds() {
      const selectedSquads = this.cachedSquads.filter(
        (squad) =>
          this.squadIds.includes(String(squad.id)) ||
          this.squadIds.includes(squad.id),
      );

      this.squadNamesFromSquadIds = selectedSquads.map((squad) => squad.name);
      // Loading directly to modal from URL cached squads are empty, so wait for event
      if (this.squadIds.length != 1) {
        this.showTeamBreakdown = true;
      } else {
        this.showTeamBreakdown = this.cachedSquads.find(
          (squad) => squad.id == this.squadIds[0],
        )?.is_parent;
      }
    },
    get visibleDrilldownTabs() {
      let filteredTabs = drilldownTabs.filter((drilldownTab) => {
        if (drilldownTab.tab == "team_breakdown") {
          return this.showTeamBreakdown && !this.isFilteringByGroups();
        }
        // Related drivers requires at least one driver
        if (drilldownTab.tab == "related_driver")
          return this.selectedWorkflow?.factor_id;
        if (drilldownTab.tab == "related_driver_comments")
          return this.selectedWorkflow?.factor_id;
        // Recommendations requires an article
        if (drilldownTab.tab == "recommendations")
          return this.selectedWorkflow?.atlas_article_id;

        return true;
      });

      return filteredTabs;
    },
    get tagNamesFromTagIds() {
      const selectedTags = tagOptions.filter(
        (tag) =>
          this.tagIds.includes(String(tag.id)) || this.tagIds.includes(tag.id),
      );

      return selectedTags.map((tag) => tag.name);
    },
    get driverComments() {
      if (this.commentSearchResults.length > 0) {
        return this.commentSearchResults;
      }

      return this.allComments || [];
    },
    get showOrgComparison() {
      return this.$store.snapshotResultFilters.squadIds.length > 0;
    },
    get showScores() {
      return this.responseCount >= minResponseThreshold;
    },
    get compareTo() {
      return this.$store.snapshotResultFilters.compareTo;
    },
    get previousSnapshotId() {
      return this.$store.snapshotResultFilters.previousSnapshotId;
    },
    get sortReverse() {
      return this.$store.snapshotResultFilters.sortReverse;
    },
    get sortBy() {
      return this.$store.snapshotResultFilters.sortBy;
    },
    get drilldownSortBy() {
      return this.$store.snapshotResultFilters.drilldownSortBy;
    },
    get drilldownSortReverse() {
      return this.$store.snapshotResultFilters.drilldownSortReverse;
    },
    get tagIds() {
      return this.$store.snapshotResultFilters.tagIds;
    },
    get hasMultipleTrendValues() {
      const validValues = this?.trends?.values?.filter(
        (v) => v.value !== null && v.value !== undefined && !isNaN(v.value),
      );
      return validValues?.length >= 2;
    },
    get hasMultipleTrendValuesForDriver() {
      const validValues = this?.relatedDriverTrends?.[0]?.values?.filter(
        (v) => v.value !== null && v.value !== undefined && !isNaN(v.value),
      );
      return validValues?.length >= 2;
    },
    get allComments() {
      return this.drilldownData?.related_driver_comments?.data?.comments || [];
    },
    clearSearchTerm() {
      this.commentSearchTerm = "";
      this.commentSearchResults = [];
      this.searchCommentsLoaded = false;
    },
    performCommentSearch() {
      this.searchCommentsLoaded = false;
      const finalTerm = this.commentSearchTerm.trim();

      if (finalTerm.length > 2) {
        const queryParamObject = {
          term: finalTerm,
          factor_id: this.selectedWorkflow.factor_id,
          branch: this.$store.snapshotResultFilters.branch,
          squad_ids: this.squadIds.join(","),
          group_ids: this.groupIds.join(","),
          tag_ids: this.tagIds.join(","),
        };

        const fullUrl = `${baseUrl}/comments/search?${objectToQueryString(queryParamObject)}`;

        return fetch(fullUrl)
          .then((resp) => resp.json())
          .then((data) => {
            this.commentSearchResults = data;
            this.searchCommentsLoaded = true;
          })
          .catch((error) => {
            console.log("error", error);
          });
      } else {
        this.commentSearchResults = [];
      }
    },
    percentageWidthForPosition(workflow, position) {
      return calcPercentageWidthForPosition(workflow, position);
    },
    countForPosition(workflow, position) {
      return calcCountForPosition(workflow, position);
    },
    workflowPercentageOfResponses(workflow, position) {
      return calcWorkflowPercentageOfResponses(workflow, position);
    },
    targetedPositions(workflow) {
      return calcTargetedPositions(workflow, maxBehavioralQuestions);
    },
    nonTargetedPositions(workflow) {
      return calcNonTargetedPositions(workflow, maxBehavioralQuestions);
    },
    percentageOfResponses(positionCount, totalFieldCount) {
      return calcPercentageOfResponses(
        this.selectedWorkflow,
        positionCount,
        totalFieldCount,
      );
    },
    modalTitle() {
      // Prefer explicit behavioral question name if provided, otherwise fallback to generic name
      return (
        this.selectedWorkflow?.behavioral_question_name ||
        this.selectedWorkflow?.name
      );
    },
    openDrilldownModal(selectedWorkflow) {
      // Don't show any modals if not enough responses
      if (this.responseCount < minResponseThreshold) return;
      this.drilldownModalOpen = true;
      // Defaults to overview tab when opening modal
      this.selectedDrilldownTab = "overview";
      this.targetedWorkflowId = selectedWorkflow.id;
      this.selectedWorkflow = selectedWorkflow;

      setQueryStringParam("behavioral_question_id", selectedWorkflow.id);
      setQueryStringParam("ddt", "overview");
      this.fetchTab(this.drilldownTabs[0].tab, this.drilldownTabs[0].url);
    },
    hideWorkflowDetails() {
      this.drilldownModalOpen = false;
      this.targetedWorkflowId = null;
      this.selectedWorkflow = null;
      this.selectedDrilldownTab = null;
      // Clear the data so we don't run into weird cache issues.
      this.drilldownData = {};
      this.trends = null;
      this.commentSearchTerm = "";
      this.commentSearchResults = [];
      this.searchCommentsLoaded = false;

      setQueryStringParam("ddt", null);
      setQueryStringParam("behavioral_question_id", null);
    },
    get workflowItems() {
      const items = this.resultItems.sort(
        sortFunc(this.sortBy, this.compareTo, {
          orgScore: this.selectedWorkflow?.org_score,
          reverse: this.sortReverse,
        }),
      );

      // Skip global reverse for columns that handle reverse internally to preserve null positioning
      const fieldsWithInternalReverse = [
        "annualizedCost",
        "score",
        "calculatedComparison",
        "avgValueBenchmark",
      ];
      if (
        this.sortReverse &&
        !fieldsWithInternalReverse.includes(this.sortBy)
      ) {
        return items.reverse();
      }

      return items;
    },
    workflowBenchmarkDiff(workflow, compareTo) {
      return calcWorkflowBenchmarkDiff(workflow, compareTo);
    },
    isSortOrder(order) {
      return this.sortBy == order;
    },
    isDrilldownSortOrder(order) {
      return this.drilldownSortBy == order;
    },
    changeComparison(compareTo) {
      this.$store.snapshotResultFilters.setCompareTo(compareTo);
      if (this.compareTo == "vs_prev") this.fetchAll();

      // Refresh team breakdown data for industry benchmark changes
      if (
        this.selectedDrilldownTab == "team_breakdown" &&
        this.drilldownModalOpen
      ) {
        this.refetchTeamBreakdown();
      }

      // Update any trends comparison data if it's showing
      if (this.selectedDrilldownTab == "overview" && this.drilldownModalOpen) {
        renderWorkflowAverageValueTrendChart({
          workflow: this.selectedWorkflow,
          trendData: this.trends,
          elChartId: "workflow-average-value-trend-chart",
          xStart: this.xStart,
          compareTo: this.compareTo,
        });
        renderWorkflowAnnualizedCostTrendChart({
          workflow: this.selectedWorkflow,
          trendData: this.trends,
          elChartId: "workflow-annualized-cost-trend-chart",
          xStart: this.xStart,
          compareTo: this.compareTo,
        });
      }
    },
    changeSortBy(sortBy) {
      if (this.sortBy == sortBy) {
        this.$store.snapshotResultFilters.sortReverse = !this.sortReverse;

        setQueryStringParam("sb", sortBy);
        setQueryStringParam("sr", this.sortReverse);
        return;
      }

      this.$store.snapshotResultFilters.sortBy = sortBy;
      this.$store.snapshotResultFilters.sortReverse = false;

      setQueryStringParam("sb", sortBy);
      setQueryStringParam("sr", false);
    },
    changeDrilldownSortBy(sortBy) {
      if (this.drilldownSortBy == sortBy) {
        this.$store.snapshotResultFilters.drilldownSortReverse =
          !this.drilldownSortReverse;

        setQueryStringParam("ddsr", this.drilldownSortReverse);
        return;
      }

      this.$store.snapshotResultFilters.drilldownSortBy = sortBy;
      this.$store.snapshotResultFilters.drilldownSortReverse = false;

      setQueryStringParam("ddsb", sortBy);
      setQueryStringParam("ddsr", false);
    },
    // Note: changeSquadIds and changeGroupIds methods are provided by peopleFiltersMixin

    changeTagIds(tagIds) {
      this.$store.snapshotResultFilters.tagIds = tagIds;
      setQueryStringParam("tag_ids", tagIds);

      this.fetchAll();

      // Only clear tree state and refresh team breakdown if modal is actually open
      if (
        this.selectedDrilldownTab === "team_breakdown" &&
        this.drilldownModalOpen
      ) {
        this.resetTreeState();
        this.refetchTeamBreakdown();
      }
    },
    percentageOfResponsesOverview(positionCount, totalFieldCount) {
      const item = this.relatedDriver;

      if (!item) return;

      if (item[totalFieldCount] === 0) return "0%";

      return `${Math.round(
        (100 * item[positionCount]) / item[totalFieldCount],
      )}%`;
    },
    fetchAll() {
      return this.fetchResultItems();
    },
    fetchTab(tab, url) {
      if (tab === "team_breakdown") {
        this.resetTreeState();
        this.isFetchingTableData = true;

        // Set default drilldown sort if not specified in URL
        if (!getQueryStringParams().get("ddsb")) {
          this.$store.snapshotResultFilters.drilldownSortBy =
            "snapshotSquadName";
          this.$store.snapshotResultFilters.drilldownSortReverse = false;
          setQueryStringParam("ddsb", "snapshotSquadName");
          setQueryStringParam("ddsr", "false");
        }
      }

      // Make this smarter so if we've already fetched the tab for the SAME factor we don't fetch it again
      this.selectedDrilldownTab = tab;
      setQueryStringParam("ddt", tab);

      return fetch(`${url}${this.queryParams()}`)
        .then((resp) => resp.json())
        .then((data) => {
          if (tab == "overview" && data.trends.length > 0) {
            const benchmarkBehavioralQuestionAverage =
              data.benchmark_behavioral_question_averages[
                data.trends[0].workflow["id"]
              ];

            if (benchmarkBehavioralQuestionAverage) {
              data.trends[0].workflow["benchmark_50"] =
                benchmarkBehavioralQuestionAverage["p_50"];
              data.trends[0].workflow["benchmark_75"] =
                benchmarkBehavioralQuestionAverage["p_75"];
              data.trends[0].workflow["benchmark_90"] =
                benchmarkBehavioralQuestionAverage["p_90"];
            }
          }

          if (tab == "related_driver") {
            const benchmarkBehavioralQuestion =
              data.benchmark_factors[data.driver["id"]];

            if (benchmarkBehavioralQuestion) {
              data.driver_trends[0].factor["benchmark_50"] =
                benchmarkBehavioralQuestion.p_50;
              data.driver_trends[0].factor["benchmark_75"] =
                benchmarkBehavioralQuestion.p_75;
              data.driver_trends[0].factor["benchmark_90"] =
                benchmarkBehavioralQuestion.p_90;

              data.driver["benchmark_50"] = benchmarkBehavioralQuestion.p_50;
              data.driver["benchmark_75"] = benchmarkBehavioralQuestion.p_75;
              data.driver["benchmark_90"] = benchmarkBehavioralQuestion.p_90;
            }
          }

          this.drilldownData[tab] = { data };
          if (tab === "team_breakdown") {
            this.tableDataLoaded = true;
            this.isFetchingTableData = false;
          }

          this.tabDataLoaded = true;
          this.renderTabDetails(tab, data);
        });
    },
    renderTabDetails(tab, data) {
      if (tab == "overview") {
        if (data.trends.length > 0) {
          this.trends = data.trends[0];
          this.xStart = this.trends.values[0].date;
          renderWorkflowAverageValueTrendChart({
            workflow: this.selectedWorkflow,
            trendData: this.trends,
            elChartId: "workflow-average-value-trend-chart",
            xStart: this.xStart,
            compareTo: this.compareTo,
          });
          renderWorkflowAnnualizedCostTrendChart({
            workflow: this.selectedWorkflow,
            trendData: this.trends,
            elChartId: "workflow-annualized-cost-trend-chart",
            xStart: this.xStart,
            compareTo: this.compareTo,
          });
          renderWorkflowPercentInTargetRangeTrendChart({
            workflow: this.selectedWorkflow,
            trendData: this.trends,
            elChartId: "workflow-percent-in-target-range-trend-chart",
            xStart: this.xStart,
            compareTo: this.compareTo,
          });
          // Render distribution chart for average section
          renderWorkflowDistributionTrendChart({
            workflow: this.selectedWorkflow,
            trendData: this.trends,
            elChartId: "workflow-average-value-distribution-chart",
            xStart: this.xStart,
            compareTo: this.compareTo,
            hasMultipleTrendValues: this.hasMultipleTrendValues,
          });
          // Render distribution chart for percent in target section
          renderWorkflowDistributionTrendChart({
            workflow: this.selectedWorkflow,
            trendData: this.trends,
            elChartId: "workflow-percent-in-target-range-distribution-chart",
            xStart: this.xStart,
            compareTo: this.compareTo,
            hasMultipleTrendValues: this.hasMultipleTrendValues,
          });
        } else {
          this.trends = null;
        }
      }

      if (tab == "related_driver") {
        this.relatedDriver = data.driver;
        this.relatedDriverTrends = data.driver_trends;
        // Find first valid value (skip null values)
        const firstValid = this.relatedDriverTrends[0].values.find(
          (v) => v.value !== null && v.value !== undefined && !isNaN(v.value),
        );
        this.xStart = firstValid
          ? firstValid.date
          : this.relatedDriverTrends[0].values[0].date;

        // Re-render charts after data is fetched
        setTimeout(() => this.renderRelatedDriverCharts(), 50);
      }
    },
    handleRenderOverviewCharts({ section }) {
      // Handle event from workflowOverviewTabs to re-render charts
      if (section === "average") {
        renderWorkflowAverageValueTrendChart({
          workflow: this.selectedWorkflow,
          trendData: this.trends,
          elChartId: "workflow-average-value-trend-chart",
          xStart: this.xStart,
          compareTo: this.compareTo,
        });
        renderWorkflowDistributionTrendChart({
          workflow: this.selectedWorkflow,
          trendData: this.trends,
          elChartId: "workflow-average-value-distribution-chart",
          xStart: this.xStart,
          compareTo: this.compareTo,
          hasMultipleTrendValues: this.hasMultipleTrendValues,
        });
      } else if (section === "annualized_cost") {
        renderWorkflowAnnualizedCostTrendChart({
          workflow: this.selectedWorkflow,
          trendData: this.trends,
          elChartId: "workflow-annualized-cost-trend-chart",
          xStart: this.xStart,
          compareTo: this.compareTo,
        });
      } else if (section === "percent_in_target") {
        renderWorkflowPercentInTargetRangeTrendChart({
          workflow: this.selectedWorkflow,
          trendData: this.trends,
          elChartId: "workflow-percent-in-target-range-trend-chart",
          xStart: this.xStart,
          compareTo: this.compareTo,
        });
        renderWorkflowDistributionTrendChart({
          workflow: this.selectedWorkflow,
          trendData: this.trends,
          elChartId: "workflow-percent-in-target-range-distribution-chart",
          xStart: this.xStart,
          compareTo: this.compareTo,
          hasMultipleTrendValues: this.hasMultipleTrendValues,
        });
      }
    },
    xStartLabel() {
      if (!this.xStart) return "";
      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      }).format(new Date(this.xStart));
    },
    get atlasArticle() {
      if (this.drilldownData.recommendations?.data) {
        return this.drilldownData.recommendations.data.article;
      }

      return null;
    },
    relatedDriverOptionLabel(position) {
      let label;

      if (this.relatedDriver?.scale_points == 3) {
        if (position == 1) label = "Bad";
        if (position == 2) label = "So-so";
        if (position == 3) label = "Good";
      }

      if (this.relatedDriver?.scale_points == 5) {
        if (position == 1) label = "Never";
        if (position == 2) label = "Rarely";
        if (position == 3) label = "Sometimes";
      }

      return label;
    },
    queryParams() {
      const paramObj = {
        ct: this.compareTo,
        prev_ss: this.previousSnapshotId,
        squad_ids: `${this.squadIds.join(",")}`,
        group_ids: `${this.groupIds.join(",")}`,
        tag_ids: `${this.tagIds.join(",")}`,
        behavioral_question_id: this.targetedWorkflowId,
        branch: this.$store.snapshotResultFilters.branch,
        aggregation:
          this.$store.snapshotResultFilters.aggregation ||
          getQueryStringParams().get("aggregation"),
      };

      return `?${objectToQueryString(paramObj)}`;
    },
    calculateItemComparison(item) {
      if (
        ["vs_50th", "vs_75th", "vs_90th"].includes(this.compareTo) &&
        item.benchmark_50 == null
      )
        return null;

      if (this.compareTo == "vs_50th") {
        return item.score - item.benchmark_50;
      } else if (this.compareTo == "vs_75th") {
        return item.score - item.benchmark_75;
      } else if (this.compareTo == "vs_90th") {
        return item.score - item.benchmark_90;
      } else if (this.compareTo == "vs_prev") {
        return item.previous_score != null
          ? item.score - item.previous_score
          : null;
      } else if (this.compareTo == "vs_org") {
        return item.score - this.selectedWorkflow?.org_score;
      }
    },
    calculateDriverComparison(item) {
      if (
        ["vs_50th", "vs_75th", "vs_90th"].includes(this.compareTo) &&
        item.benchmark_50 == null
      )
        return null;

      if (this.compareTo == "vs_50th") {
        return item.score - item.benchmark_50;
      } else if (this.compareTo == "vs_75th") {
        return item.score - item.benchmark_75;
      } else if (this.compareTo == "vs_90th") {
        return item.score - item.benchmark_90;
      } else if (this.compareTo == "vs_prev") {
        return item.previous_score != null
          ? item.score - item.previous_score
          : null;
      } else if (this.compareTo == "vs_org") {
        return item.score - item.org_score;
      }
    },
    get breakdownItems() {
      return this.getBreakdownItems({
        sortBy: this.drilldownSortBy,
        compareTo: this.compareTo,
        reverse: this.drilldownSortReverse,
        orgScore: this.selectedWorkflow?.org_score,
      });
    },
    workflowTrendEnd() {
      if (!this.trends?.values?.length) return;

      return this.trends.values[this.trends.values.length - 1].label;
    },
    renderDriverTrendChart() {
      const trends = this.relatedDriverTrends[0];
      if (!trends) return null;

      if (trends.values && trends.values.length > 0) {
        this.xEnd = trends.values[trends.values.length - 1]?.label;
      }

      return renderDriverTrendChartPure({
        trends: trends,
        elChartId: "driver-trends-chart",
        xStart: this.xStart,
        compareTo: this.compareTo,
        orgScore: this.relatedDriver?.org_score,
      });
    },
    renderDriverDistributionTrendChart() {
      const trends = this.relatedDriverTrends[0];
      if (!trends) return null;

      return renderDriverDistributionTrendChartPure({
        driver: this.relatedDriver,
        trendData: trends,
        elChartId: "driver-distribution-trends-chart",
        xStart: this.xStart,
        hasMultipleTrendValues: this.hasMultipleTrendValuesForDriver,
      });
    },
    renderRelatedDriverCharts() {
      if (!this.relatedDriver || !this.relatedDriverTrends?.length) return;

      this.renderDriverTrendChart();
      this.renderDriverDistributionTrendChart();
    },
    get atlasStrategies() {
      if (this.drilldownData.recommendations?.data) {
        return this.drilldownData.recommendations.data.strategies;
      }

      return [];
    },
    get noSearchResults() {
      return (
        this.commentSearchResults.length == 0 &&
        this.commentSearchTerm &&
        this.searchCommentsLoaded
      );
    },
    fetchResultItems() {
      this.resultItemsLoaded = false;

      let url = `${baseUrl}/items`;

      const paramsObj = {
        ct: this.compareTo,
        prev_ss: this.previousSnapshotId,
        squad_ids: this.squadIds.join(","),
        group_ids: this.groupIds.join(","),
        tag_ids: this.tagIds.join(","),
        branch: this.$store.snapshotResultFilters.branch,
      };

      const queryParams = objectToQueryString(paramsObj);

      return fetch(`${url}?${queryParams}`)
        .then((resp) => resp.json())
        .then((data) => {
          // Benchmark data now provided by server - no client-side processing needed

          this.resultItems = data.result_items;
          let responseCount = 0;
          data.result_items.forEach((row) => {
            if (row.total_responses > (responseCount || 0))
              responseCount = row.total_responses;
          });
          this.responseCount = responseCount;

          // Update selectedWorkflow with fresh data if modal is open
          if (this.selectedWorkflow && this.targetedWorkflowId) {
            this.selectedWorkflow = this.resultItems.find(
              (row) => row.id == this.targetedWorkflowId,
            );
          }

          this.resultItemsLoaded = true;
        });
    },
    refetchTeamBreakdown() {
      // Reset tree state when aggregation or filters change
      this.resetTreeState();

      // Set fetching state
      this.isFetchingTableData = true;

      const selectedItem = drilldownTabs.find(
        (t) => t.tab == this.selectedDrilldownTab,
      );

      this.fetchTab(this.selectedDrilldownTab, selectedItem.url);
    },
    handleAggregationChange(detail) {
      if (detail.methodName && typeof this[detail.methodName] === "function") {
        this[detail.methodName]();
      }
    },
    handleComparisonChange() {
      this.fetchAll();
      const currentTab = this.selectedDrilldownTab;
      const tabConfig = this.visibleDrilldownTabs.find(
        (t) => t.tab === currentTab,
      );

      if (currentTab && tabConfig?.url) {
        this.fetchTab(currentTab, tabConfig.url);
      }
    },

    /**
     * Required by teamTreeExpansionMixin - provides the API endpoint for fetching child team data
     * when expanding team rows in the breakdown table. Do not remove this method.
     * @returns {string} The URL endpoint for child data requests
     */
    getChildDataEndpoint() {
      const teamBreakdownTab = this.drilldownTabs?.find(
        (t) => t.tab === "team_breakdown",
      );
      return teamBreakdownTab?.url;
    },

    /**
     * Required by teamTreeExpansionMixin - provides query parameters for fetching child team data
     * when expanding team rows. Do not remove this method.
     * @returns {Object} Parameters object containing filters and context for the API request
     */
    getChildDataParams() {
      const currentParams = Object.fromEntries(
        getQueryStringParams().entries(),
      );
      return {
        ...currentParams,
        tag_ids: this.tagIds.join(","),
        group_ids: this.groupIds.join(","),
        behavioral_question_id: this.selectedWorkflow?.id,
        prev_ss: this.$store?.snapshotResultFilters?.previousSnapshotId,
        branch: this.$store?.snapshotResultFilters?.branch,
      };
    },

    /**
     * Required by teamTreeExpansionMixin - provides cached squad data for parent/child lookups
     * and team hierarchy navigation. Do not remove this method.
     * @returns {Array} Array of squad objects with id, parent_id, is_parent properties
     */
    getCachedSquads() {
      return this.cachedSquads || [];
    },

    numberWithCommas(value) {
      return numberWithCommas(value, 0);
    },

    // Get group names for tooltips using mixin resolver
    getGroupNames() {
      return this.resolveGroupNames(this.groups, this.getCurrentGroupIds());
    },

    driverDistributionItems() {
      if (!this.relatedDriver) return [];

      const driver = this.relatedDriver;
      const totalCount = driver.count || 0;
      const scalePoints = driver.scale_points;
      const items = [];

      // For 5-point scale, include positions 5 and 4
      if (scalePoints === 5) {
        items.push({
          label: "Always",
          count: driver.five_count || 0,
          percentage:
            totalCount > 0
              ? `${Math.round((driver.five_count / totalCount) * 100)}%`
              : "0%",
          targeted: false,
        });
        items.push({
          label: "Very often",
          count: driver.four_count || 0,
          percentage:
            totalCount > 0
              ? `${Math.round((driver.four_count / totalCount) * 100)}%`
              : "0%",
          targeted: false,
        });
      }

      // Always include positions 3, 2, 1
      items.push({
        label: this.relatedDriverOptionLabel(3),
        count: driver.three_count || 0,
        percentage:
          totalCount > 0
            ? `${Math.round((driver.three_count / totalCount) * 100)}%`
            : "0%",
        targeted: false,
      });
      items.push({
        label: this.relatedDriverOptionLabel(2),
        count: driver.two_count || 0,
        percentage:
          totalCount > 0
            ? `${Math.round((driver.two_count / totalCount) * 100)}%`
            : "0%",
        targeted: false,
      });
      items.push({
        label: this.relatedDriverOptionLabel(1),
        count: driver.one_count || 0,
        percentage:
          totalCount > 0
            ? `${Math.round((driver.one_count / totalCount) * 100)}%`
            : "0%",
        targeted: false,
      });

      return items;
    },
  };
}
